import { deleteData, fetchData, uploadData } from "api/apiHelpers";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Document } from "types";
import logger from "utils/logger";

const useDocuments = () => {
  const { id } = useParams();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [file, setFile] = useState<File | null>(null);

  // Fetch documents for patient
  const fetchDocuments = async () => {
    try {
      const data = await fetchData<Document[]>(
        `/patient-documents/?patient=${id}`
      );
      setDocuments(data);
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") {
        logger.debug("Fetch aborted, safe to ignore.");
        return;
      }
      logger.error("Error fetching documents:", error);
    }
  };

  // Upload handler
  const handleUpload = async (
    uploadFile: File | null = null,
    extraFiles: File[] = []
  ) => {
    const formData = new FormData();

    if (file || uploadFile) {
      formData.append("file", uploadFile || file!);
    }
    if (extraFiles.length > 0 && !uploadFile) {
      extraFiles.forEach((f) => formData.append("file", f));
    }

    if ((file || extraFiles.length > 0 || uploadFile) && id) {
      formData.append("patient", id); // match backend field
      try {
        await uploadData(`/patient-documents/`, formData);
        setFile(null);
        fetchDocuments();
      } catch (error) {
        logger.error("Error uploading file:", error);
      }
    }
  };

  // Delete handler with debouncing protection
  const [deletingIds, setDeletingIds] = useState<Set<string>>(new Set());
  
  const handleDelete = async (docId: string) => {
    // Prevent multiple simultaneous deletes of the same document
    if (deletingIds.has(docId)) {
      logger.debug("[Delete] Already deleting document:", docId);
      return;
    }

    try {
      setDeletingIds((prev) => new Set(prev).add(docId));
      await deleteData(`/patient-documents/${docId}/`);
      setDocuments((prev) => prev.filter((doc) => doc.id !== docId));
      logger.debug("[Delete] Successfully deleted document:", docId);
    } catch (error: unknown) {
      logger.error("[Delete] Error deleting document:", error);
      const errMsg = error instanceof Error ? error.message : "Unknown error";
      alert(`Failed to delete document: ${errMsg}`);
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(docId);
        return next;
      });
    }
  };

  // Auto-fetch when patient id changes
  useEffect(() => {
    if (id) fetchDocuments();
  }, [id]);

  return {
    documents,
    file,
    setFile,
    fetchDocuments,
    handleUpload,
    handleDelete,
  };
};

export default useDocuments;
